import { defineStore } from 'pinia';

import WebSocketService, { WebSocketServiceOptions } from '@/services/WebSocketService';
import { authStoreEvents, useAuthStore } from '@/store/authStore';

export const useWebSocketStore = defineStore('webSocketStore', {
    state: () => ({
        connected: false,
        deviceId: '',
        visibilityChangeListenerAdded: false,
        wsService: null as WebSocketService | null,
        listenersRegistered: false,
    }),

    actions: {
        initialize(accessToken: string) {
            if (WebSocketService.isActive()) {
                console.log('❌ WebSocketService is already initialized.');
                return this.wsService;
            }

            if (!this.deviceId) {
                this.deviceId = this.generateDeviceId();
            }

            const options: WebSocketServiceOptions = {
                accessToken,
                environment: 'dev',
                heartbeatInterval: 10000,
                deviceId: this.deviceId,
            };

            this.wsService = WebSocketService.getInstance(options);
            if (!this.wsService) {
                console.error('WebSocketService initialization failed.');
                return;
            }
            this.wsService.connect();
            this.wsService.on('connected', () => {
                this.connected = true;
            });
            this.wsService.on('disconnected', () => {
                this.connected = false;
            });

            // Listen for token refresh events and update WebSocketService
            authStoreEvents.on('tokenRefreshed', (newAccessToken) => {
                if (!this.wsService) {
                    console.error('WebSocketService not initialized.');
                    return;
                }
                this.wsService.updateAccessToken(newAccessToken);
                console.log('WebSocketService accessToken updated on refresh.');
            });

            if (!this.visibilityChangeListenerAdded && this.wsService) {
                this.visibilityChangeListenerAdded = true;

                // Stop ws connections when user navigates away
                // and restart when user comes back
                document.addEventListener('visibilitychange', async () => {
                    const authStore = useAuthStore();
                    if (!this.wsService) {
                        console.error('WebSocketService not initialized.');
                        return;
                    }
                    if (document.hidden) {
                        console.log('Page hidden - closing WebSocket connection.');
                        this.wsService.close();
                    } else {
                        console.log('Page visible - re-enabling WebSocket connection.');
                        let accessToken = authStore.getAccessToken();
                        if (!accessToken) {
                            console.info('No access token available.');
                            return;
                        }
                        // Check that authtokens are still valid
                        authStore.showTokenState();
                        if (authStore.isAccessTokenExpired()) {
                            console.log('Token expired - refreshing tokens.');
                            await authStore.refreshTokens();
                            accessToken = authStore.getAccessToken();
                        }

                        if (accessToken) {
                            this.wsService.updateAccessToken(accessToken);
                            this.wsService.connect();
                        }
                    }
                });
            }

            return this.wsService;
        },

        generateDeviceId(): string {
            return (crypto as any).randomUUID();
        },
        close(): void {
            if (this.wsService) {
                this.wsService.close();
                this.wsService = null;
            }
        },
    },
});
